/**
 * Client for re-running auth-on-file checks (queue-first with direct API fallback).
 * @module reRunAuthCheck
 */

import { FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE } from "../../constants/stringLiterals";
import { RPATaskRequest, RPATaskResponse } from "../../data-model/rpaTaskQueue";
import { API_ENDPOINTS, RPA_PATHS_ONLY_FOR_QUEUE } from "../../enums/apiUrls";
import { PortalIdForRabbitMq } from "../../enums/portalIdsForRabbitMq";
import { logDataToConsole } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";
import { sendRpaRequestsToRabbitMq } from "./sendRpaRequestsToRabbitMq";

/** Patient and order context sent to the auth-on-file check endpoint or queue. */
export interface ReRunAuthCheckPayload {
  patient_id: string;
  regimen_name: string;
  order_id: string;
  date_of_service: string;
  org_id: string;
}

/**
 * Sends a direct API request to re-run auth on file check.
 */
const sendDirectApiRequest = async (
  body: ReRunAuthCheckPayload,
  token: string,
): Promise<RPATaskResponse> => {
  const response = await fetch(API_ENDPOINTS.AUTH_ON_FILE_CHECK, {
    method: "POST",
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to re-run auth on file check");
  }

  await response.json();
  return {
    success: true,
    message: "Auth on file check initiated successfully",
  };
};

/**
 * Builds the RPA task request payload for the queue.
 */
const buildRpaTaskRequest = (
  body: ReRunAuthCheckPayload,
  token: string,
): RPATaskRequest => ({
  payload: body,
  org_id: body.org_id,
  portal_id: PortalIdForRabbitMq.ONCOEMR,
  priority: FRONT_END_PRIORITY_VALUE_FOR_RABBITMQ_QUEUE,
  identifier: body?.order_id ?? "",
  order_id: body?.order_id ?? "",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/**
 * Re-runs auth on file check via RPA queue with direct API fallback.
 * First attempts to queue the request via RabbitMQ. If that fails,
 * falls back to a direct API call.
 */
export const reRunAuthOnFileCheck = async (
  body: ReRunAuthCheckPayload,
): Promise<RPATaskResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const rpaRequest = buildRpaTaskRequest(body, token);
    const queueResponse = await sendRpaRequestsToRabbitMq(
      RPA_PATHS_ONLY_FOR_QUEUE.AUTH_ON_FILE_CHECK,
      rpaRequest,
    );

    if (queueResponse.success) {
      return queueResponse;
    }

    // Fallback to direct API call if queue fails
    return await sendDirectApiRequest(body, token);
  } catch (error) {
    logDataToConsole("error in reRunAuthOnFileCheck", error);
    throw error;
  }
};
